import React from 'react';

import {
  minValue,
  NumberInput,
  ReferenceArrayInput,
  required,
  SelectArrayInput,
  SelectInput,
  useRecordContext,
} from 'react-admin';

import { Grid, Typography } from '@mui/material';

import { useWatch } from 'react-hook-form';
import { isBoolean } from 'lodash';

import BooleanRadioGroupInput from '../../../../customFields/BooleanRadioGroupInput';
import { recommendationsChoices } from '../../../../constants/recommendations';
import { mapBinaryRule } from '../../ruleFeatures';
import ruleTypes from '../../../../constants/ruleTypes';
import { RuleNames } from '../../../../constants/ruleNames';
import { slaUnitsOfTimeChoices } from '../../../../constants/slaUnitsOfTime';

type Form = {
  name: RuleNames;
  ruleType: string;
}

const RuleResponse = () => {
  const ruleName = useWatch<Form, 'name'>({ name: 'name' });
  const record = useRecordContext();
  const ruleType = useWatch<Form, 'ruleType'>({ name: 'ruleType' });
  const createPing = useWatch({ name: 'createPing', defaultValue: isBoolean(record?.createPing) ? record?.createPing : true });
  const isAutoConfirmed = useWatch({ name: 'isAutoConfirmed', defaultValue: record?.isAutoConfirmed ?? false });
  const shouldOffsetDeadline = useWatch({ name: 'shouldOffsetDeadline', defaultValue: record?.shouldOffsetDeadline });

  return (
    <Grid container>
      <Grid size={12}>
        <Typography variant="label">
          If an error occurs, should a ping be created?
        </Typography>
      </Grid>
      <Grid size={12}>
        <BooleanRadioGroupInput
          label={false}
          source="pingOnError"
          defaultValue
          validate={required()}
        />
      </Grid>
      {ruleType === ruleTypes.REQUEST && (
        <>
          <Grid size={12}>
            <Typography variant="label">
              If check is positive, which recommendation should be returned?
            </Typography>
          </Grid>
          <Grid size={12}>
            <SelectInput
              source="recommendation"
              fullWidth
              validate={required()}
              choices={recommendationsChoices}
              label="Recommendation"
            />
          </Grid>
        </>
      )}
      {ruleType === ruleTypes.REQUEST && (
        <>
          <Grid size={12}>
            <Typography variant="label">
              If check is positive, which, if any, signal(s) should be returned?
            </Typography>
          </Grid>
          <Grid size={12}>
            <ReferenceArrayInput perPage={100} source="signals" reference="signals" filter={{ active: true }}>
              <SelectArrayInput source="name" fullWidth />
            </ReferenceArrayInput>
          </Grid>
        </>
      )}
      <Grid size={12}>
        <Typography variant="label">
          If check is positive, create Ping?
        </Typography>
      </Grid>
      <Grid size={12}>
        <BooleanRadioGroupInput
          label={false}
          defaultValue
          source="createPing"
          validate={required()}
        />
      </Grid>
      {createPing && (
        <>
          <Grid container>
            <Grid size={12}>
              <Typography variant="label">
                If Ping is created, should it be confirmed automatically?
              </Typography>
            </Grid>
            <Grid size={12}>
              <BooleanRadioGroupInput
                label={false}
                defaultValue={false}
                source="isAutoConfirmed"
                validate={required()}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid size={12}>
              <Typography variant="label">
                If Ping is created, should it have a deadline offset?
              </Typography>
            </Grid>
            <Grid size={12}>
              <BooleanRadioGroupInput
                label={false}
                defaultValue={false}
                source="shouldOffsetDeadline"
              />
            </Grid>
          </Grid>
          {shouldOffsetDeadline && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="label">
                  What should the deadline offset be?
                </Typography>
              </Grid>
              <Grid size={6}>
                <NumberInput
                  source="deadlineOffset.amount"
                  fullWidth
                  label="Number"
                  validate={[required(), minValue(1)]}
                />
              </Grid>
              <Grid size={6}>
                <SelectInput
                  source="deadlineOffset.unitOfTime"
                  choices={slaUnitsOfTimeChoices}
                  fullWidth
                  label="Unit of time"
                  validate={required()}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}
      {(isAutoConfirmed && createPing && mapBinaryRule[ruleName]) && (
        <Grid container>
          <Grid size={12}>
            <Typography variant="label">
              For how long should the system use this decision for future requests?
              (During that time pings won&apos;t be created for that rule and
              recommendation will be inferred from the decision)
            </Typography>
          </Grid>
          <Grid size={12}>
            <NumberInput
              source="numberOfDaysIgnoreRule"
              label="Days"
              validate={[required(), minValue(0)]}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default RuleResponse;
